import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Switch,
  FormControlLabel, Divider, Alert, CircularProgress, Backdrop
} from '@mui/material';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState({
    salon_name: 'Bella Vista',
    salon_address: '123 Main St, City, State 12345',
    salon_phone: '+1 (555) 123-4567',
    salon_email: 'info@salon.com',
    business_hours_start: '09:00',
    business_hours_end: '17:00',
    appointment_buffer: '15',
    reminder_hours: '24',
    max_advance_booking: '30',
    email_notifications: 'true',
    sms_notifications: 'true',
    auto_confirmation: 'false',
    allow_online_booking: 'true',
    // Home page content
    hero_title: 'Welcome to Bella Vista',
    hero_subtitle: 'Experience luxury beauty and wellness services in a serene, professional environment. Your journey to radiance begins here.',
    about_text: 'Bella Vista Salon & Spa offers premium beauty and wellness services in a luxurious environment.',
    // Services
    service_hair_title: 'Hair Services',
    service_hair_desc: 'Professional cutting, coloring, styling, and treatments for all hair types. From classic cuts to modern trends.',
    service_facial_title: 'Facial Treatments',
    service_facial_desc: 'Rejuvenating facials, anti-aging treatments, and skincare solutions tailored to your skin\'s unique needs.',
    service_nail_title: 'Nail Care',
    service_nail_desc: 'Manicures, pedicures, gel applications, and nail art. Complete hand and foot care in a relaxing environment.',
    service_makeup_title: 'Makeup Services',
    service_makeup_desc: 'Professional makeup for special occasions, bridal services, and makeup consultations with expert artists.',
    service_spa_title: 'Spa Treatments',
    service_spa_desc: 'Relaxing massages, body treatments, and wellness therapies to rejuvenate your mind and body.',
    service_premium_title: 'Premium Packages',
    service_premium_desc: 'Comprehensive beauty packages combining multiple services for the ultimate pampering experience.',
    // Social media
    facebook_url: '#',
    instagram_url: '#',
    twitter_url: '#',
    youtube_url: '#'
  });

  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/settings');
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });
      
      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Salon Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Salon Name"
                  value={settings.salon_name || ''}
                  onChange={(e) => handleChange('salon_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={settings.salon_address || ''}
                  onChange={(e) => handleChange('salon_address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={settings.salon_phone || ''}
                  onChange={(e) => handleChange('salon_phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={settings.salon_email || ''}
                  onChange={(e) => handleChange('salon_email', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Working Hours
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={settings.business_hours_start || '09:00'}
                  onChange={(e) => handleChange('business_hours_start', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={settings.business_hours_end || '17:00'}
                  onChange={(e) => handleChange('business_hours_end', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Appointment Buffer (minutes)"
                  type="number"
                  value={settings.appointment_buffer || '15'}
                  onChange={(e) => handleChange('appointment_buffer', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reminder Hours"
                  type="number"
                  value={settings.reminder_hours || '24'}
                  onChange={(e) => handleChange('reminder_hours', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Max Advance Booking (days)"
                  type="number"
                  value={settings.max_advance_booking || '30'}
                  onChange={(e) => handleChange('max_advance_booking', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Home Page Content
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hero Title"
                  value={settings.hero_title || ''}
                  onChange={(e) => handleChange('hero_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hero Subtitle"
                  multiline
                  rows={3}
                  value={settings.hero_subtitle || ''}
                  onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="About Text"
                  multiline
                  rows={2}
                  value={settings.about_text || ''}
                  onChange={(e) => handleChange('about_text', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Hair & Facial Services
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hair Service Title"
                  value={settings.service_hair_title || ''}
                  onChange={(e) => handleChange('service_hair_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hair Service Description"
                  multiline
                  rows={2}
                  value={settings.service_hair_desc || ''}
                  onChange={(e) => handleChange('service_hair_desc', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Facial Service Title"
                  value={settings.service_facial_title || ''}
                  onChange={(e) => handleChange('service_facial_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Facial Service Description"
                  multiline
                  rows={2}
                  value={settings.service_facial_desc || ''}
                  onChange={(e) => handleChange('service_facial_desc', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Other Services
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nail Service Title"
                  value={settings.service_nail_title || ''}
                  onChange={(e) => handleChange('service_nail_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nail Service Description"
                  multiline
                  rows={2}
                  value={settings.service_nail_desc || ''}
                  onChange={(e) => handleChange('service_nail_desc', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Makeup Service Title"
                  value={settings.service_makeup_title || ''}
                  onChange={(e) => handleChange('service_makeup_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Spa Service Title"
                  value={settings.service_spa_title || ''}
                  onChange={(e) => handleChange('service_spa_title', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Social Media Links
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Facebook URL"
                  value={settings.facebook_url || ''}
                  onChange={(e) => handleChange('facebook_url', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram URL"
                  value={settings.instagram_url || ''}
                  onChange={(e) => handleChange('instagram_url', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Twitter URL"
                  value={settings.twitter_url || ''}
                  onChange={(e) => handleChange('twitter_url', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="YouTube URL"
                  value={settings.youtube_url || ''}
                  onChange={(e) => handleChange('youtube_url', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_notifications === 'true'}
                      onChange={(e) => handleChange('email_notifications', e.target.checked.toString())}
                    />
                  }
                  label="Email Notifications"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sms_notifications === 'true'}
                      onChange={(e) => handleChange('sms_notifications', e.target.checked.toString())}
                    />
                  }
                  label="SMS Notifications"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.auto_confirmation === 'true'}
                      onChange={(e) => handleChange('auto_confirmation', e.target.checked.toString())}
                    />
                  }
                  label="Auto-confirm Appointments"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allow_online_booking === 'true'}
                      onChange={(e) => handleChange('allow_online_booking', e.target.checked.toString())}
                    />
                  }
                  label="Allow Online Booking"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" size="large" onClick={handleSave}>
              Save Settings
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
